import { useNavigate } from 'react-router';
import { useScheduleContext } from '../../Contexts/ScheduleContext';
import { AppRoutes } from '../../ApplicationRoutes';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
	Button,
	Divider,
	MenuItem,
	Select,
	type SelectChangeEvent,
	Stack,
	Typography,
} from '@mui/material';
import apiClient from '../../ApiClient';
import { Schedule } from '@mui/icons-material';
import SchedulePostInput from './SchedulePostInput';
import type { TiktokAccount } from '../Schedule/SchedulePage';

export default function SelectSchedulePage() {
	const [tiktokAccounts, setTiktokAccounts] = useState<TiktokAccount[]>([]);
	const [scheduleButtonCounter, setScheduleButtonCounter] = useState<number>(0);
	const { selectedGroup, selectedTiktokAccount, setSelectedTiktokAccount, setPostTimes } =
		useScheduleContext();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				if (selectedGroup.length === 0) {
					navigate(AppRoutes.Schedule);
					return;
				}
				await fetchTiktokAccounts();
			} catch (error) {
				console.error('Error fetching data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [selectedGroup, navigate]);

	useEffect(() => {
		if (!selectedGroup || !selectedTiktokAccount) return;

		const fetchSchedule = async () => {
			try {
				const res = await apiClient.get('/core/recurringschedule/by-group/', {
					params: {
						group: selectedGroup,
						tiktok_account: selectedTiktokAccount.id,
					},
				});
				const data = res.data;

				const times = [
					data.post_time_1,
					data.post_time_2,
					data.post_time_3,
					data.post_time_4,
					data.post_time_5,
				]
					.filter(Boolean)
					.map(t => {
						const [hours, minutes] = t.split(':');
						return dayjs().hour(parseInt(hours)).minute(parseInt(minutes)).second(0);
					});

				setPostTimes(prev => {
					const newArr = new Array(5).fill(null);
					times.forEach((t, i) => (newArr[i] = t));
					return newArr;
				});
				setScheduleButtonCounter(times.length);
			} catch (err: any) {
				if (err?.response?.status === 404) {
					setPostTimes(new Array(5).fill(null));
					setScheduleButtonCounter(0);
				} else {
					console.error('Error fetching schedule:', err);
				}
			}
		};

		fetchSchedule();
	}, [selectedGroup, selectedTiktokAccount, setPostTimes]);

	const handleAddScheduleClick = () => {
		if (scheduleButtonCounter === 5) return;
		setScheduleButtonCounter(prev => prev + 1);
	};

	const fetchTiktokAccounts = async () => {
		const response = await apiClient.get('/core/tiktokaccounts/');
		const data = response.data as TiktokAccount[];
		setTiktokAccounts(data);
	};

	const handleTiktokAccountSelect = (event: SelectChangeEvent<string>) => {
		const id = Number.parseInt(event.target.value, 10);
		const selectedAccount = tiktokAccounts.find(account => account.id === id);
		if (selectedAccount) {
			setSelectedTiktokAccount(selectedAccount);
		}
	};

	return (
		<Stack direction={'column'} flex={1} divider={<Divider orientation="horizontal" flexItem />}>
			<Stack direction={'row'} justifyContent={'space-between'} paddingX={'20px'} paddingY={'20px'}>
				<Stack direction={'column'}>
					<Typography fontSize={'25px'} fontWeight={'bold'}>
						New Schedule
					</Typography>
				</Stack>
			</Stack>
			<Stack
				direction={'column'}
				justifyContent={'space-between'}
				paddingX={'20px'}
				paddingY={'20px'}
				gap={5}
			>
				<div className="tiktok-account flex flex-col gap-4">
					<div>
						<h1 className="font-semibold text-xl text-neutral-700">Select TikTok account</h1>
						<p className="text-sm text-neutral-500">
							Choose a Tiktok account to post your video group to
						</p>
					</div>
					<Select
						id="tiktok-account-select"
						value={selectedTiktokAccount ? String(selectedTiktokAccount.id) : ''}
						sx={{
							minWidth: 200,
							maxWidth: 300,
						}}
						disabled={isLoading}
						onChange={handleTiktokAccountSelect}
					>
						{tiktokAccounts.length > 0 ? (
							tiktokAccounts.map(account => (
								<MenuItem key={account.id} value={String(account.id)}>
									{account.username}
								</MenuItem>
							))
						) : (
							<MenuItem disabled value="">
								No TikTok accounts available
							</MenuItem>
						)}
					</Select>
				</div>
				<div className="flex flex-col gap-4">
					<div>
						<h1 className="font-semibold text-xl text-neutral-700">Add video schedule times</h1>
						<p className="text-sm text-neutral-500">You may set up to 5 different times</p>
					</div>

					{[...Array(scheduleButtonCounter)].map((_, i) => (
						<SchedulePostInput
							key={i}
							id={i}
							onDelete={() => {
								setPostTimes(prev => {
									const updated = [...prev];
									updated.splice(i, 1); // remove the time
									while (updated.length < 5) updated.push(null); // pad back to 5 slots
									return updated;
								});
								setScheduleButtonCounter(prev => Math.max(prev - 1, 0));
							}}
						/>
					))}

					<Button
						variant="outlined"
						startIcon={<Schedule />}
						onClick={handleAddScheduleClick}
						sx={{
							width: 'fit-content',
						}}
					>
						Add Schedule
					</Button>
					<Button
						onClick={() => navigate('/video-upload')}
						sx={{
							width: 'fit-content',
						}}
						variant="outlined"
					>
						Next
					</Button>
				</div>
			</Stack>
		</Stack>
	);
}
